import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { Checkbox, Form, StandardZipInput, validationRules } from 'reaxl-forms';
import { Button, Glyphicon, Select } from 'reaxl';
import { useMakeDropdown, useModelDropdown, useZipcodeInput, useSearchSubmit, useModelOptions } from '@/contexts/VehicleSearchContext';

validationRules();

const renderCheckBoxes = (checkBoxes) => {
    const items = () => checkBoxes.map((checkBox, index) => {
        const {
            label,
            glyph,
            checked,
            glyphClassName = '',
            labelClassName = '',
        } = checkBox;

        const domNameID = `searchBadgeCheckbox${index}`;

        return (
            <Checkbox
                key={domNameID}
                name={domNameID}
                id={domNameID}
                rowClassName="display-inline-block padding-right-3"
                labelClassName="display-inline-block padding-right-2"
                value={checked}
                label={(
                    <div className="display-flex align-items-top">
                        <span className={`${labelClassName} display-inline-block padding-right-2`}>{label}</span>
                        <span className={`${glyphClassName} display-inline-block`}><Glyphicon glyph={glyph} /></span>
                    </div>
                )}
            />
        );
    });

    return (
        <div className="row padding-left-0 padding-left-sm-2">
            {items()}
        </div>
    );
};

const SearchBadge = React.memo(({
    badgeClassName,
    badgeSubTitle = '',
    badgeTitle = 'Cars for Sale - Search Near You',
    browseByLinks,
    checkBoxes = [],
    className,
    collapsed,
    componentClass: Component = 'h1',
    device,
    labelClassName,
    includeInputLabels = false,
    makeConfig,
    modelConfig,
    zipConfig,
    submitConfig,
    showLocationModal,
    titleClassName,
    dataCmp = 'SearchBadge',
    ...rest
}) => {
    const { make, makeOptions, handleMakeChange } = useMakeDropdown();
    const { model, handleModelChange } = useModelDropdown();
    const { zipcode, handleZipcodeChange } = useZipcodeInput();
    const { handleSearchSubmit } = useSearchSubmit();
    const modelOptions = useModelOptions();

    const inputTopClassName = clsx({
        'col-sm-4': !collapsed,
    });

    const inputBottomClassName = clsx({
        'col-sm-2': !collapsed,
    });

    const titleClassNames = clsx(titleClassName, 'text-size-sm-650');

    const badgeRowClassNames = clsx(badgeClassName, 'row');

    return (
        <div
            data-cmp={dataCmp}
            className={className}
            {...rest}
        >
            <div className={badgeRowClassNames}>
                <div>
                    <div className="col-xs-12 col-sm-6">
                        <Component
                            className={`${titleClassNames} display-inline text-nowrap`}
                            data-cmp="txt-hi-header"
                            role="heading"
                            aria-level={1}
                        >
                            <span className="display-flex">
                                <span>{badgeTitle}</span>
                            </span>
                        </Component>
                        {badgeSubTitle && (
                            <h2>{badgeSubTitle}</h2>
                        )}
                    </div>
                    {browseByLinks && (
                        <div className="margin-top-sm-4 col-xs-12 col-sm-6 text-nowrap margin-bottom-0 margin-top-0 padding-right-sm-">
                            {browseByLinks}
                        </div>
                    )}
                </div>
                <Form
                    onSubmit={handleSearchSubmit}
                    role="form"
                >
                    <div className="col-xs-12 margin-top-2 inputContainer">
                        <div className={`${makeConfig?.css || 'col-xs-6 padding-left-0'} ${inputTopClassName}`}>
                            <Select
                                aria-label={`Select ${makeOptions[0]}`}
                                name="makeCode"
                                value={make.value}
                                options={makeOptions}
                                help={makeConfig?.help || ''}
                                onChange={(e) => handleMakeChange(e, makeConfig?.analyticsData)}
                                labelClassName={labelClassName}
                                label={includeInputLabels ? 'Make' : undefined}
                            />
                        </div>
                        <div className={`${modelConfig?.css || 'col-xs-6 padding-horizontal-0 padding-horizontal-sm-2'} ${inputTopClassName}`}>
                            <Select
                                aria-label={`Select ${modelOptions[0]}`}
                                name="ModelCode"
                                value={model.value}
                                options={modelOptions}
                                help={modelConfig?.help || ''}
                                onChange={(e) => handleModelChange(e, modelConfig?.analyticsData)}
                                labelClassName={labelClassName}
                                label={includeInputLabels ? 'Model' : undefined}
                            />
                        </div>
                        <div className={`${zipConfig?.css || 'col-xs-6 padding-left-0 padding-left-sm-2 padding-top-3 padding-top-sm-0'} ${inputBottomClassName}`}>
                            <StandardZipInput
                                aria-label="Select ZIP Code"
                                name="zipcode"
                                placeholder="ZIP Code"
                                value={zipcode}
                                onChange={handleZipcodeChange}
                                required
                                labelClassName={labelClassName}
                                label={includeInputLabels ? 'ZIP Code' : undefined}
                            />
                        </div>
                        <div className={`${submitConfig?.css || 'col-xs-6 padding-top-3 padding-top-sm-0 padding-horizontal-0 padding-horizontal-sm-2'} ${inputBottomClassName}`}>
                            <Button
                                block
                                id="search"
                                bsStyle="primary-inverse"
                                type="submit"
                            >
                                Search
                            </Button>
                        </div>
                    </div>
                    {checkBoxes.length > 0 && renderCheckBoxes(checkBoxes)}
                </Form>
            </div>
        </div>
    );

});

SearchBadge.propTypes = {
    /**
     * The className to be applied for root
     */
    className: PropTypes.string,
    /**
     * The labelClassName to be applied for input labels
     */
    labelClassName: PropTypes.string,
    /**
     * The badgeClassName to be applied to the search badge root
     */
    badgeClassName: PropTypes.string,
    /**
     * The titleClassName to be applied to the search badge title
     */
    titleClassName: PropTypes.string,
    /**
     * Props for an array of checkboxes
     */
    checkBoxes: PropTypes.array,
    /**
     * Determines collapsed mode to persist mobile layout
     */
    collapsed: PropTypes.bool,
    /**
     * Props for make select option dropdown
     */
    makeConfig: PropTypes.shape({
        analyticsData: PropTypes.object,
        css: PropTypes.string,
        help: PropTypes.node,
    }),
    /**
     * Props for model select option dropdown
     */
    modelConfig: PropTypes.shape({
        analyticsData: PropTypes.object,
        css: PropTypes.string,
        help: PropTypes.node,
    }),
    /**
     * Props for submit button
     */
    submitConfig: PropTypes.shape({
        css: PropTypes.string,
    }),
    /**
     * The badgeTitle used for h1 text
     */
    badgeTitle: PropTypes.string,
    /**
     * The badgeTitle used for h1 text
     */
    badgeSubTitle: PropTypes.string,
    /**
     * The browseByLinks node to be right of h1 text
     */
    browseByLinks: PropTypes.node,

    /**
     * componentClass to allow defining of wrapping element
     */
    componentClass: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.element,
        PropTypes.func,
    ]),
};

export default SearchBadge;
